// ── PendingImportsPanel — Inbound migration queue requiring admin approval ──
import React, { useState } from 'react';
import {
  Inbox,
  Clock,
  ChevronDown,
  ChevronRight,
  Eye,
  Users,
  Loader2,
  Trash2,
} from 'lucide-react';
import type {
  PendingImportItem,
  Tenant,
} from '../types';

interface PendingImportsPanelProps {
  items: PendingImportItem[];
  isLoading: boolean;
  tenantList: Tenant[];

  // Review — loads manifest into the right panel for inspection
  onReview: (id: string) => void;
  reviewLoading: string | null; // id currently being fetched

  // Approve — opens approval popup for a specific pending item
  onApprove: (id: string) => void;

  // Reject / discard
  onReject: (id: string) => void;
  rejectLoading: string | null;
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function PendingImportsPanel({
  items,
  isLoading,
  tenantList,
  onReview,
  reviewLoading,
  onApprove,
  onReject,
  rejectLoading,
}: PendingImportsPanelProps) {
  const [expanded, setExpanded] = useState(true);

  if (items.length === 0 && !isLoading) return null;

  return (
    <div className="border-t border-amber-900/40 bg-amber-950/20">
      {/* Header */}
      <button
        type="button"
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-amber-950/30 transition-colors"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center gap-2 text-xs font-medium text-amber-300">
          <Inbox className="w-3.5 h-3.5" />
          Pending Imports
          <span className="px-1.5 py-0.5 rounded-full bg-amber-900/60 text-[9px] text-amber-200 tabular-nums font-mono">
            {items.length}
          </span>
        </div>
        {expanded ? (
          <ChevronDown className="w-3 h-3 text-amber-500/60" />
        ) : (
          <ChevronRight className="w-3 h-3 text-amber-500/60" />
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-2 space-y-1.5">
          {isLoading && items.length === 0 && (
            <div className="flex items-center gap-2 text-[10px] text-neutral-500 py-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading…
            </div>
          )}

          {items.map((item) => (
            <div
              key={item.id}
              className="pac-glass-subtle/60 rounded p-2.5 space-y-2"
            >
              {/* Item header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[10px]">
                  <Clock className="w-3 h-3 text-amber-500/60" />
                  <span className="text-neutral-400">
                    {timeAgo(item.queued_at)}
                  </span>
                  {item.source_env && (
                    <>
                      <span className="text-neutral-700">·</span>
                      <span className="text-neutral-500">
                        from{' '}
                        <span className="text-neutral-300 font-mono">
                          {item.source_env}
                        </span>
                      </span>
                    </>
                  )}
                </div>
                <span className="text-[9px] text-neutral-600 font-mono truncate max-w-[100px]">
                  {item.id.slice(0, 8)}…
                </span>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-1.5 text-[10px]">
                <div className="pac-glass rounded px-1.5 py-1">
                  <div className="text-[8px] text-neutral-600 uppercase">
                    Scopes
                  </div>
                  <div className="text-neutral-200 tabular-nums">
                    {item.scope_count}
                  </div>
                </div>
                <div className="pac-glass rounded px-1.5 py-1">
                  <div className="text-[8px] text-neutral-600 uppercase">
                    Outcomes
                  </div>
                  <div className="text-neutral-200 tabular-nums">
                    {item.outcome_count}
                  </div>
                </div>
                <div className="pac-glass rounded px-1.5 py-1">
                  <div className="text-[8px] text-neutral-600 uppercase">
                    NS
                  </div>
                  <div className="text-neutral-200 tabular-nums">
                    {item.namespace_summary.length}
                  </div>
                </div>
              </div>

              {/* Namespace summary */}
              {item.namespace_summary.length > 0 && (
                <div className="text-[9px] text-neutral-600 truncate">
                  {item.namespace_summary
                    .slice(0, 3)
                    .map((n) => `${n.ns} (${n.count})`)
                    .join(', ')}
                  {item.namespace_summary.length > 3
                    ? ` +${item.namespace_summary.length - 3} more`
                    : ''}
                </div>
              )}

              {/* Root scope */}
              {item.root_scope && (
                <div className="text-[9px] text-neutral-600 font-mono truncate">
                  root: {item.root_scope.scope_namespace}/
                  {item.root_scope.scope_key}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-1.5 pt-0.5">
                <button
                  className={
                    'px-2 py-1 rounded border text-[10px] flex items-center gap-1 transition-colors ' +
                    (reviewLoading === item.id
                      ? 'border-neutral-700 text-neutral-500 cursor-wait'
                      : 'border-neutral-700 text-neutral-300 hover:border-neutral-500')
                  }
                  disabled={reviewLoading === item.id}
                  onClick={() => onReview(item.id)}
                >
                  {reviewLoading === item.id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Eye className="w-3 h-3" />
                  )}
                  Review
                </button>
                <button
                  className="px-2 py-1 rounded border border-violet-800 text-[10px] text-violet-300 hover:border-violet-600 flex items-center gap-1 transition-colors"
                  onClick={() => onApprove(item.id)}
                >
                  <Users className="w-3 h-3" />
                  Approve
                </button>
                <button
                  className={
                    'px-2 py-1 rounded border text-[10px] flex items-center gap-1 transition-colors ' +
                    (rejectLoading === item.id
                      ? 'border-neutral-800 text-neutral-600 cursor-wait'
                      : 'border-neutral-800 text-neutral-600 hover:border-rose-800 hover:text-rose-400')
                  }
                  disabled={rejectLoading === item.id}
                  onClick={() => onReject(item.id)}
                >
                  {rejectLoading === item.id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Trash2 className="w-3 h-3" />
                  )}
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
